import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy } from 'react';
import { subscribeMock } from '@/dev/mockState';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AppShell } from '@/layouts/AppShell';
import { Gated } from '@/features/plans/LockedPage';
import { ThemeProvider } from './providers/Theme';
import { ToastProvider } from './providers/Toast';
import { AuthBoundary } from '@/features/auth/AuthBoundary';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { ScrollRails } from '@/layouts/ScrollRails';
import { pageLoader } from './routes';

const OverviewPage = lazy(pageLoader('/'));
const NewsPage = lazy(pageLoader('/news'));
const CalendarPage = lazy(pageLoader('/calendar'));
const WallStreetPage = lazy(pageLoader('/wall-street'));
const PortfolioPage = lazy(pageLoader('/portfolio'));
const SignalsPage = lazy(pageLoader('/signals'));
const SocialPage = lazy(pageLoader('/social'));
const OrcaAIPage = lazy(pageLoader('/ai'));
const SettingsPage = lazy(pageLoader('/settings'));
const ProfilePage = lazy(pageLoader('/profile'));
const AlertsPage = lazy(pageLoader('/alerts'));
const WhalesPage = lazy(pageLoader('/whales'));


// Every route exists on every plan. A page that IS a paid feature shows its
// preview when the plan doesn't open it (same check as every other entry point).
const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'news', element: <NewsPage /> },
      { path: 'calendar', element: <Gated feature="economic_calendar"><CalendarPage /></Gated> },
      { path: 'wall-street', element: <Gated feature="wall_street"><WallStreetPage /></Gated> },
      { path: 'portfolio', element: <PortfolioPage /> },
      { path: 'signals', element: <SignalsPage /> },
      { path: 'alerts', element: <Gated feature="price_alerts"><AlertsPage /></Gated> },
      { path: 'whales', element: <Gated feature="whale_alerts"><WhalesPage /></Gated> },
      { path: 'social', element: <Gated feature="twitter"><SocialPage /></Gated> },
      { path: 'ai', element: <OrcaAIPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

// Mock data is local, so it never goes stale; real API will set its own policy.
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: Infinity, retry: false } } });

// DEV: switching a mock scenario reloads page data (skeleton → new state);
// shell data (account, Orca AI, notifications) refreshes in place.
const SHELL = ['account', 'orca-ai', 'notices', 'plans', 'teasers'];
if (import.meta.env.DEV) {
  subscribeMock(() => {
    void queryClient.invalidateQueries({ predicate: (q) => SHELL.includes(String(q.queryKey[0])) });
    void queryClient.resetQueries({ predicate: (q) => !SHELL.includes(String(q.queryKey[0])) });
  });
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <ScrollRails />
            <AuthBoundary>
              <RouterProvider router={router} />
            </AuthBoundary>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
